import { zodResolver } from '@hookform/resolvers/zod';
import {
  AREA_UNITS,
  WEB_LOCATION_MODES,
  OPERATIONS,
  PROPERTY_FIELD_LABELS,
  PROPERTY_TYPES,
  formatDistrict,
  formatPhone,
  needsRentPrice,
  needsSalePrice,
  propertySchema,
  type Operation,
  type PropertyField,
  type PropertyType,
} from '@erp/shared';
import { LocateFixed, Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Link } from 'react-router';
import type { z } from 'zod';

import { api, errorMessage } from '../../lib/api.ts';
import {
  useAssignees,
  useContacts,
  useDistrictSearch,
  type ContactDetail,
} from '../../lib/contacts-api.ts';
import { applyApiErrors } from '../../lib/form-errors.ts';
import type { PropertyDetail, PropertyDuplicateMatch } from '../../lib/properties-api.ts';
import { useSession } from '../../lib/session.ts';
import { Alert } from '../ui/alert.tsx';
import { Button } from '../ui/button.tsx';
import { Card } from '../ui/card.tsx';
import { Combobox } from '../ui/combobox.tsx';
import { Dialog } from '../ui/dialog.tsx';
import { Field, Input, Select } from '../ui/field.tsx';

type Values = z.input<typeof propertySchema>;
type Output = z.output<typeof propertySchema>;
type SensitiveMode = 'keep' | 'change' | 'remove';

const at = <T extends string>(prefix: T, index: number) =>
  `${prefix}.${String(index)}` as `${T}.${number}`;

/** Empty input → null; otherwise a number (the schema validates it). */
const asNumber = {
  setValueAs: (v: unknown) => (v === '' || v === null || v === undefined ? null : Number(v)),
};
const textarea = 'w-full rounded-lg border border-border bg-surface p-3 text-base sm:text-sm';

const MATCH_LABELS = {
  address: 'dirección',
  owner: 'propietario',
  cadastralCode: 'código catastral',
} as const;

/** "13.6769, -89.2797" (as copied from a map) → coordinates; null if it is not that. */
function parseCoordinates(text: string): { lat: number; lng: number } | null {
  const match = /^\s*(-?\d{1,2}(?:\.\d+)?)\s*[,;\s]\s*(-?\d{1,3}(?:\.\d+)?)\s*$/.exec(text);
  if (!match?.[1] || !match[2]) return null;
  return { lat: Number(match[1]), lng: Number(match[2]) };
}

/**
 * Create or edit a property. The exact address and the map point are hidden once
 * stored (replace or remove, never shown here). Owners appear only for whoever may
 * see them; otherwise they are kept as they are.
 */
export function PropertyForm({
  property,
  onSaved,
  submitLabel,
}: {
  property?: PropertyDetail;
  onSaved: (saved: PropertyDetail) => void;
  submitLabel: string;
}) {
  const { data: session } = useSession();
  const assignees = useAssignees();
  const [error, setError] = useState<string | null>(null);
  const [duplicates, setDuplicates] = useState<PropertyDuplicateMatch[]>([]);
  const [addressMode, setAddressMode] = useState<SensitiveMode>(
    property?.hasAddress ? 'keep' : 'change',
  );
  const [locationMode, setLocationMode] = useState<SensitiveMode>(
    property?.hasLocation ? 'keep' : 'change',
  );
  const [coordinates, setCoordinates] = useState('');
  const [coordinatesError, setCoordinatesError] = useState<string | null>(null);
  const [districtQuery, setDistrictQuery] = useState(
    property
      ? formatDistrict({
          name: property.district.name,
          municipality: { id: 0, name: property.district.municipality },
        })
      : '',
  );
  const districts = useDistrictSearch(districtQuery);
  // Owners are editable only when the API showed them (sensitive.view).
  const ownersEditable = property?.owners !== null;
  const [ownerNames, setOwnerNames] = useState<Record<string, string>>(
    Object.fromEntries((property?.owners ?? []).map((o) => [o.contactId, o.name])),
  );

  const c = property?.characteristics;
  const form = useForm<Values, unknown, Output>({
    resolver: zodResolver(propertySchema),
    defaultValues: {
      title: property?.title ?? '',
      description: property?.description ?? '',
      titleEn: property?.titleEn ?? '',
      descriptionEn: property?.descriptionEn ?? '',
      type: property?.type ?? 'house',
      operation: property?.operation ?? 'sale',
      salePrice: property?.salePrice ?? null,
      rentPrice: property?.rentPrice ?? null,
      ...(property ? { districtId: property.district.id } : {}),
      neighborhood: property?.neighborhood ?? '',
      cadastralCode: property?.cadastralCode ?? '',
      videoUrl: property?.videoUrl ?? '',
      virtualTourUrl: property?.virtualTourUrl ?? '',
      bedrooms: c?.bedrooms ?? null,
      bathrooms: c?.bathrooms ?? null,
      parkingSpaces: c?.parkingSpaces ?? null,
      builtArea: c?.builtArea ?? null,
      lotArea: c?.lotArea ?? null,
      lotAreaUnit: property?.lotAreaUnit ?? 'm2',
      webLocationMode: property?.webLocationMode ?? 'approximate',
      builtAreaUnit: property?.builtAreaUnit ?? 'm2',
      levels: c?.levels ?? null,
      floorNumber: c?.floorNumber ?? null,
      maintenanceFee: c?.maintenanceFee ?? null,
      lotFront: c?.lotFront ?? null,
      lotDepth: c?.lotDepth ?? null,
      yearBuilt: c?.yearBuilt ?? null,
      owners:
        property?.owners?.map((o) => ({ contactId: o.contactId, sharePct: o.sharePct })) ?? [],
      assignedMembershipId: property?.assignedTo.membershipId ?? session?.membership.id,
      capturedByMembershipId: property?.capturedBy.membershipId ?? session?.membership.id,
      exclusive: property?.exclusive ?? false,
      listingStartsOn: property?.listingStartsOn ?? null,
      listingEndsOn: property?.listingEndsOn ?? null,
      commissionSalePct: property?.commission.salePct ?? null,
      commissionRentMonths: property?.commission.rentMonths ?? null,
      networkSharePct: property?.commission.networkSharePct ?? null,
      commissionConditions: property?.commission.conditions ?? '',
      keysCount: property?.keysCount ?? null,
      keysLocation: property?.keysLocation ?? '',
      privateNotes: property?.privateNotes ?? '',
    },
  });
  const owners = useFieldArray({ control: form.control, name: 'owners' });
  const errors = form.formState.errors;
  const type = useWatch({ control: form.control, name: 'type' });
  const operation = useWatch({ control: form.control, name: 'operation' });
  const exclusive = useWatch({ control: form.control, name: 'exclusive' });

  const checkDuplicates = async (extraOwner?: string) => {
    const values = form.getValues();
    const body = {
      address: addressMode === 'change' && values.address ? values.address : null,
      cadastralCode: values.cadastralCode ?? null,
      ownerIds: [
        ...(values.owners ?? []).map((o) => o.contactId),
        ...(extraOwner ? [extraOwner] : []),
      ],
      ...(property ? { excludeId: property.id } : {}),
    };
    if (!body.address && !body.cadastralCode && body.ownerIds.length === 0) {
      setDuplicates([]);
      return;
    }
    try {
      setDuplicates(
        await api<PropertyDuplicateMatch[]>('/properties/duplicates', { method: 'POST', body }),
      );
    } catch {
      // Only a hint: a failed check does not block the form.
    }
  };

  const addOwner = (contact: { id: string; name: string }) => {
    if ((form.getValues('owners') ?? []).some((o) => o.contactId === contact.id)) return;
    setOwnerNames((names) => ({ ...names, [contact.id]: contact.name }));
    owners.append({ contactId: contact.id, sharePct: null });
    form.clearErrors('owners');
    void checkDuplicates(contact.id);
  };

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    let location: { lat: number; lng: number } | null | undefined;
    if (locationMode === 'keep') location = undefined;
    else if (locationMode === 'remove' || !coordinates.trim()) location = null;
    else {
      location = parseCoordinates(coordinates);
      if (!location) {
        setCoordinatesError('Escribe latitud y longitud, por ejemplo 13.6769, -89.2797');
        return;
      }
    }
    const body = {
      ...values,
      // Undefined keeps what is stored; null removes it.
      address:
        addressMode === 'keep' ? undefined : addressMode === 'remove' ? null : values.address,
      location,
      owners: ownersEditable ? values.owners : undefined,
    };
    try {
      const saved = property
        ? await api<PropertyDetail>(`/properties/${property.id}`, { method: 'PATCH', body })
        : await api<PropertyDetail>('/properties', { method: 'POST', body });
      onSaved(saved);
    } catch (err) {
      setError(
        applyApiErrors(err, form.setError, [
          'title',
          'salePrice',
          'rentPrice',
          'districtId',
          'owners',
          'listingEndsOn',
          'assignedMembershipId',
          'capturedByMembershipId',
        ]),
      );
    }
  });

  const numberField = (field: PropertyField, options: { step?: string; unit?: string } = {}) => (
    <Field
      key={field}
      label={
        options.unit
          ? `${PROPERTY_FIELD_LABELS[field]} (${options.unit})`
          : PROPERTY_FIELD_LABELS[field]
      }
      error={errors[field]?.message}
    >
      {(a11y) => (
        <Input
          {...a11y}
          type="number"
          inputMode="decimal"
          step={options.step ?? '1'}
          min={field === 'floorNumber' ? -5 : 0}
          {...form.register(field, asNumber)}
        />
      )}
    </Field>
  );
  // Each area with its unit: land in v² and construction in m² is common.
  const areaField = (field: 'builtArea' | 'lotArea') => {
    const unitName = field === 'lotArea' ? 'lotAreaUnit' : 'builtAreaUnit';
    return (
      <Field key={field} label={PROPERTY_FIELD_LABELS[field]} error={errors[field]?.message}>
        {(a11y) => (
          <div className="flex gap-2">
            <Input
              {...a11y}
              className="min-w-0 flex-1"
              type="number"
              inputMode="decimal"
              step="0.01"
              min={0}
              {...form.register(field, asNumber)}
            />
            <Select
              aria-label={field === 'lotArea' ? 'Unidad del terreno' : 'Unidad de construcción'}
              className="w-auto"
              {...form.register(unitName)}
            >
              {Object.entries(AREA_UNITS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </div>
        )}
      </Field>
    );
  };
  const fields = PROPERTY_TYPES[type].fields as readonly PropertyField[];

  return (
    <form onSubmit={(event) => void onSubmit(event)} className="space-y-6" noValidate>
      {error && <Alert tone="danger">{error}</Alert>}

      <Card className="space-y-4">
        <h2 className="font-semibold">Datos generales</h2>
        <Field
          label="Título"
          hint="Lo que verá el cliente: «Casa con jardín en Santa Tecla»."
          error={errors.title?.message}
        >
          {(a11y) => <Input {...a11y} autoComplete="off" {...form.register('title')} />}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Tipo de inmueble">
            {(a11y) => (
              <Select {...a11y} {...form.register('type')}>
                {(Object.entries(PROPERTY_TYPES) as [PropertyType, { label: string }][]).map(
                  ([value, t]) => (
                    <option key={value} value={value}>
                      {t.label}
                    </option>
                  ),
                )}
              </Select>
            )}
          </Field>
          <Field label="Operación">
            {(a11y) => (
              <Select
                {...a11y}
                {...form.register('operation', {
                  onChange: (event: { target: { value: Operation } }) => {
                    const op = event.target.value;
                    if (!needsSalePrice(op)) form.setValue('salePrice', null);
                    if (!needsRentPrice(op)) form.setValue('rentPrice', null);
                  },
                })}
              >
                {(Object.entries(OPERATIONS) as [Operation, string][]).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {needsSalePrice(operation) && (
            <Field label="Precio de venta (USD)" error={errors.salePrice?.message}>
              {(a11y) => (
                <Input
                  {...a11y}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  {...form.register('salePrice', asNumber)}
                />
              )}
            </Field>
          )}
          {needsRentPrice(operation) && (
            <Field label="Precio de alquiler (USD al mes)" error={errors.rentPrice?.message}>
              {(a11y) => (
                <Input
                  {...a11y}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  {...form.register('rentPrice', asNumber)}
                />
              )}
            </Field>
          )}
        </div>
        <Field
          label="Descripción"
          hint="Para publicar en la web hace falta al menos 50 caracteres. Sin teléfonos ni correos."
        >
          {(a11y) => (
            <textarea {...a11y} rows={5} className={textarea} {...form.register('description')} />
          )}
        </Field>
        <details className="rounded-lg border border-border p-3" open={Boolean(property?.titleEn)}>
          <summary className="cursor-pointer text-sm font-medium">En inglés (sitio web)</summary>
          <div className="mt-3 space-y-4">
            <p className="text-sm text-muted-foreground">
              El sitio en inglés solo muestra las propiedades con título en inglés.
            </p>
            <Field label="Título en inglés" error={errors.titleEn?.message}>
              {(a11y) => <Input {...a11y} autoComplete="off" {...form.register('titleEn')} />}
            </Field>
            <Field label="Descripción en inglés" error={errors.descriptionEn?.message}>
              {(a11y) => (
                <textarea
                  {...a11y}
                  rows={5}
                  className={textarea}
                  {...form.register('descriptionEn')}
                />
              )}
            </Field>
          </div>
        </details>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Video (enlace, opcional)"
            hint="YouTube, Vimeo… Se abre en otra pestaña."
            error={errors.videoUrl?.message}
          >
            {(a11y) => (
              <Input {...a11y} type="url" placeholder="https://" {...form.register('videoUrl')} />
            )}
          </Field>
          <Field
            label="Recorrido virtual (enlace, opcional)"
            hint="Matterport, Kuula…"
            error={errors.virtualTourUrl?.message}
          >
            {(a11y) => (
              <Input
                {...a11y}
                type="url"
                placeholder="https://"
                {...form.register('virtualTourUrl')}
              />
            )}
          </Field>
        </div>
      </Card>

      <Card className="space-y-4">
        <h2 className="font-semibold">Ubicación</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Distrito"
            hint="Santa Tecla, Antiguo Cuscatlán…"
            error={errors.districtId?.message}
          >
            {(a11y) => (
              <Combobox
                id={a11y.id}
                describedBy={a11y['aria-describedby']}
                invalid={a11y['aria-invalid']}
                query={districtQuery}
                onQueryChange={(value) => {
                  setDistrictQuery(value);
                  if (!value) form.setValue('districtId', undefined as unknown as number);
                }}
                loading={districts.isFetching}
                options={(districts.data ?? []).map((d) => ({
                  id: String(d.id),
                  label: d.name,
                  detail: `${d.municipality.name}, ${d.department.name}`,
                }))}
                onSelect={(option) => {
                  form.setValue('districtId', Number(option.id), { shouldValidate: true });
                  setDistrictQuery(`${option.label} · ${option.detail?.split(',')[0] ?? ''}`);
                }}
                placeholder="Escribe el distrito"
              />
            )}
          </Field>
          <Field label="Zona o colonia (pública)" hint="Colonia Escalón, Residencial Las Palmas…">
            {(a11y) => <Input {...a11y} autoComplete="off" {...form.register('neighborhood')} />}
          </Field>
        </div>
        <p className="text-sm text-muted-foreground">
          La dirección exacta y el punto en el mapa son privados: se guardan cifrados, no salen en
          la web ni en la Red, y verlos queda registrado en la bitácora.
        </p>
        <SensitiveField
          label="Dirección exacta"
          stored={Boolean(property?.hasAddress)}
          mode={addressMode}
          onMode={setAddressMode}
          error={errors.address?.message}
          input={(a11y) => (
            <Input
              {...a11y}
              autoComplete="off"
              {...form.register('address', { onBlur: () => void checkDuplicates() })}
            />
          )}
        />
        <SensitiveField
          label="Coordenadas"
          stored={Boolean(property?.hasLocation)}
          mode={locationMode}
          onMode={setLocationMode}
          error={coordinatesError ?? undefined}
          input={(a11y) => (
            <div className="flex gap-2">
              <Input
                {...a11y}
                inputMode="decimal"
                placeholder="13.6769, -89.2797"
                value={coordinates}
                onChange={(e) => {
                  setCoordinates(e.target.value);
                  setCoordinatesError(null);
                }}
              />
              <Button
                variant="secondary"
                aria-label="Usar mi ubicación actual"
                onClick={() => {
                  navigator.geolocation.getCurrentPosition(
                    (position) => {
                      setCoordinates(
                        `${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)}`,
                      );
                    },
                    () => {
                      setCoordinatesError('No se pudo obtener tu ubicación; pega las coordenadas.');
                    },
                  );
                }}
              >
                <LocateFixed />
              </Button>
            </div>
          )}
          hint="Pega las coordenadas desde el mapa o usa tu ubicación si estás en la propiedad."
        />
        <Field
          label="Ubicación en el sitio web"
          hint="Aproximada: el mapa se busca por la zona y el distrito, sin marcar la casa. Sin coordenadas no hay mapa."
        >
          {(a11y) => (
            <Select {...a11y} {...form.register('webLocationMode')}>
              {Object.entries(WEB_LOCATION_MODES).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Código catastral (opcional)">
          {(a11y) => (
            <Input
              {...a11y}
              autoComplete="off"
              {...form.register('cadastralCode', { onBlur: () => void checkDuplicates() })}
            />
          )}
        </Field>
      </Card>

      <Card className="space-y-4">
        <h2 className="font-semibold">Características</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          {fields.map((field) =>
            field === 'builtArea' || field === 'lotArea'
              ? areaField(field)
              : numberField(field, {
                  ...(field === 'bathrooms' ? { step: '0.5' } : {}),
                  ...(field === 'maintenanceFee' || field === 'lotFront' || field === 'lotDepth'
                    ? { step: '0.01' }
                    : {}),
                }),
          )}
        </div>
      </Card>

      <Card className="space-y-4">
        <div>
          <h2 className="font-semibold">Propietarios</h2>
          <p className="text-sm text-muted-foreground">
            Al menos uno. Reciben el papel «Propietario» en su ficha.
          </p>
        </div>
        {!ownersEditable ? (
          <p className="text-sm text-muted-foreground">
            Tu rol no permite ver los propietarios; se conservan como están.
          </p>
        ) : (
          <>
            {(errors.owners?.root?.message ?? errors.owners?.message) && (
              <p className="text-sm text-danger" role="alert">
                {errors.owners.root?.message ?? errors.owners.message}
              </p>
            )}
            {owners.fields.map((field, index) => (
              <div key={field.id} className="grid items-end gap-2 sm:grid-cols-[1fr_9rem_auto]">
                <p className="flex min-h-11 items-center text-sm font-medium">
                  {ownerNames[field.contactId] ?? 'Contacto'}
                </p>
                <Field
                  label={`% de ${ownerNames[field.contactId] ?? 'propiedad'}`}
                  error={errors.owners?.[index]?.sharePct?.message}
                >
                  {(a11y) => (
                    <Input
                      {...a11y}
                      type="number"
                      inputMode="decimal"
                      min={0}
                      max={100}
                      step="0.01"
                      placeholder="Opcional"
                      {...form.register(`${at('owners', index)}.sharePct`, asNumber)}
                    />
                  )}
                </Field>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Quitar a ${ownerNames[field.contactId] ?? 'propietario'}`}
                  onClick={() => {
                    owners.remove(index);
                  }}
                >
                  <Trash2 />
                </Button>
              </div>
            ))}
            {owners.fields.length < 10 && <OwnerPicker onPick={addOwner} />}
          </>
        )}
        {duplicates.length > 0 && (
          <Alert tone="warning" title="Puede que esta propiedad ya exista">
            <ul className="mt-1 list-disc space-y-1 pl-5">
              {duplicates.map((d, i) => (
                <li key={d.property?.id ?? `${d.assignedTo}-${String(i)}`}>
                  Mismo {d.matchedBy.map((m) => MATCH_LABELS[m]).join(', ')}:{' '}
                  {d.property ? (
                    <Link to={`/propiedades/${d.property.id}`} className="font-medium underline">
                      {d.property.title} ({d.property.code})
                    </Link>
                  ) : (
                    <>una propiedad asignada a {d.assignedTo} (pregúntale antes de duplicarla)</>
                  )}
                </li>
              ))}
            </ul>
          </Alert>
        )}
      </Card>

      <Card className="space-y-4">
        <h2 className="font-semibold">Captación</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Agente responsable" error={errors.assignedMembershipId?.message}>
            {(a11y) => (
              <Select
                {...a11y}
                {...form.register('assignedMembershipId')}
                disabled={(assignees.data?.length ?? 0) < 2}
              >
                <MemberOptions current={property?.assignedTo} list={assignees.data} />
              </Select>
            )}
          </Field>
          <Field label="Captador" hint="Quien consiguió la propiedad (cuenta en la comisión).">
            {(a11y) => (
              <Select
                {...a11y}
                {...form.register('capturedByMembershipId')}
                disabled={(assignees.data?.length ?? 0) < 2}
              >
                <MemberOptions current={property?.capturedBy} list={assignees.data} />
              </Select>
            )}
          </Field>
        </div>
        <label className="flex min-h-11 items-center gap-2 text-sm">
          <input type="checkbox" className="size-5" {...form.register('exclusive')} />
          Exclusiva
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Inicio del acuerdo">
            {(a11y) => (
              <Input
                {...a11y}
                type="date"
                {...form.register('listingStartsOn', { setValueAs: (v: string) => v || null })}
              />
            )}
          </Field>
          <Field
            label={exclusive ? 'Vencimiento del acuerdo' : 'Vencimiento del acuerdo (opcional)'}
            hint="Te avisamos 30 días antes."
            error={errors.listingEndsOn?.message}
          >
            {(a11y) => (
              <Input
                {...a11y}
                type="date"
                {...form.register('listingEndsOn', { setValueAs: (v: string) => v || null })}
              />
            )}
          </Field>
        </div>
      </Card>

      <Card className="space-y-4">
        <div>
          <h2 className="font-semibold">Comisión acordada con el propietario</h2>
          <p className="text-sm text-muted-foreground">Se precarga al registrar el cierre.</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          {needsSalePrice(operation) && (
            <Field label="Comisión de venta (%)" error={errors.commissionSalePct?.message}>
              {(a11y) => (
                <Input
                  {...a11y}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  max={100}
                  step="0.01"
                  {...form.register('commissionSalePct', asNumber)}
                />
              )}
            </Field>
          )}
          {needsRentPrice(operation) && (
            <Field
              label="Comisión de alquiler (meses)"
              error={errors.commissionRentMonths?.message}
            >
              {(a11y) => (
                <Input
                  {...a11y}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.25"
                  {...form.register('commissionRentMonths', asNumber)}
                />
              )}
            </Field>
          )}
          <Field label="% a compartir con la Red" error={errors.networkSharePct?.message}>
            {(a11y) => (
              <Input
                {...a11y}
                type="number"
                inputMode="decimal"
                min={0}
                max={100}
                step="0.01"
                {...form.register('networkSharePct', asNumber)}
              />
            )}
          </Field>
        </div>
        <Field label="Condiciones (opcional)">
          {(a11y) => (
            <textarea
              {...a11y}
              rows={2}
              className={textarea}
              {...form.register('commissionConditions')}
            />
          )}
        </Field>
      </Card>

      <Card className="space-y-4">
        <div>
          <h2 className="font-semibold">Datos privados</h2>
          <p className="text-sm text-muted-foreground">
            Solo dentro de la inmobiliaria, para quien puede ver esta propiedad. Se guardan
            cifrados.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-[8rem_1fr]">
          <Field label="Llaves" error={errors.keysCount?.message}>
            {(a11y) => (
              <Input
                {...a11y}
                type="number"
                inputMode="numeric"
                min={0}
                {...form.register('keysCount', asNumber)}
              />
            )}
          </Field>
          <Field label="Dónde están las llaves">
            {(a11y) => <Input {...a11y} autoComplete="off" {...form.register('keysLocation')} />}
          </Field>
        </div>
        <Field label="Notas privadas">
          {(a11y) => (
            <textarea {...a11y} rows={4} className={textarea} {...form.register('privateNotes')} />
          )}
        </Field>
      </Card>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Link
          to={property ? `/propiedades/${property.id}` : '/propiedades'}
          className="inline-flex min-h-11 items-center justify-center rounded-lg px-4 text-sm font-medium hover:bg-muted"
        >
          Cancelar
        </Link>
        <Button variant="brand" type="submit" loading={form.formState.isSubmitting}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

function MemberOptions({
  current,
  list,
}: {
  current: { membershipId: string; name: string } | undefined;
  list: { membershipId: string; name: string }[] | undefined;
}) {
  return (
    <>
      {(list ?? []).map((m) => (
        <option key={m.membershipId} value={m.membershipId}>
          {m.name}
        </option>
      ))}
      {current && !list?.some((m) => m.membershipId === current.membershipId) && (
        <option value={current.membershipId}>{current.name}</option>
      )}
    </>
  );
}

/** Search the portfolio for an owner, or create the contact on the spot. */
function OwnerPicker({ onPick }: { onPick: (c: { id: string; name: string }) => void }) {
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const text = query.trim();
  const results = useContacts(text ? { q: text } : {}, text.length > 0);
  return (
    <div className="space-y-2">
      <Field label="Agregar propietario" hint="Busca en tu cartera por nombre, teléfono o correo.">
        {(a11y) => (
          <Combobox
            id={a11y.id}
            describedBy={a11y['aria-describedby']}
            query={query}
            onQueryChange={setQuery}
            loading={results.isFetching}
            options={(results.data?.items ?? []).map((c) => ({
              id: c.id,
              label: c.name,
              detail: [c.phone && formatPhone(c.phone), c.email].filter(Boolean).join(' · '),
            }))}
            onSelect={(option) => {
              onPick({ id: option.id, name: option.label });
              setQuery('');
            }}
            placeholder="Nombre, teléfono o correo"
          />
        )}
      </Field>
      <Button
        size="sm"
        variant="secondary"
        onClick={() => {
          setCreating(true);
        }}
      >
        <Plus /> Crear contacto nuevo
      </Button>
      {creating && (
        <QuickContactDialog
          initialName={text}
          onClose={() => {
            setCreating(false);
          }}
          onCreated={(contact) => {
            onPick(contact);
            setQuery('');
            setCreating(false);
          }}
        />
      )}
    </div>
  );
}

function QuickContactDialog({
  initialName,
  onClose,
  onCreated,
}: {
  initialName: string;
  onClose: () => void;
  onCreated: (c: { id: string; name: string }) => void;
}) {
  const [name, setName] = useState(initialName);
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const contact = await api<ContactDetail>('/contacts', {
        method: 'POST',
        body: { name, phones: phone.trim() ? [{ number: phone }] : [], types: [] },
      });
      onCreated({ id: contact.id, name: contact.name });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      title="Nuevo propietario"
      description="Crea el contacto con lo mínimo; completa sus datos después en su ficha."
      onClose={onClose}
    >
      <div className="space-y-4">
        {error && <Alert tone="danger">{error}</Alert>}
        <Field label="Nombre completo">
          {(a11y) => (
            <Input
              {...a11y}
              autoComplete="off"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          )}
        </Field>
        <Field label="Teléfono">
          {(a11y) => (
            <Input
              {...a11y}
              type="tel"
              inputMode="tel"
              placeholder="7123 4567"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
              }}
            />
          )}
        </Field>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="brand"
            loading={busy}
            disabled={!name.trim() || !phone.trim()}
            onClick={() => void create()}
          >
            Crear y agregar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function SensitiveField({
  label,
  stored,
  mode,
  onMode,
  error,
  hint,
  input,
}: {
  label: string;
  stored: boolean;
  mode: SensitiveMode;
  onMode: (mode: SensitiveMode) => void;
  error: string | undefined;
  hint?: string;
  input: (a11y: {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': boolean;
  }) => ReactNode;
}) {
  if (stored && mode !== 'change') {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm">
          <span className="font-medium">{label}:</span>{' '}
          {mode === 'remove' ? (
            <span className="text-danger">se quitará al guardar</span>
          ) : (
            'registrada'
          )}
        </p>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            onMode('change');
          }}
        >
          Cambiar
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            onMode(mode === 'keep' ? 'remove' : 'keep');
          }}
        >
          {mode === 'keep' ? 'Quitar' : 'Conservar'}
        </Button>
      </div>
    );
  }
  return (
    <Field
      label={label}
      error={error}
      hint={stored ? 'El valor actual se reemplazará al guardar.' : hint}
    >
      {input}
    </Field>
  );
}
